import { useEffect, useMemo, useState } from "react";
import { projectPointToRoute } from "./routeCameras.js";

function km(value) { return `${Math.round(value).toLocaleString("es-ES")} km`; }

function CameraPhoto({ camera }) {
  const [version, setVersion] = useState(() => Date.now());
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
    setVersion(Date.now());
    const timer = window.setInterval(() => { setFailed(false); setVersion(Date.now()); }, 120000);
    return () => window.clearInterval(timer);
  }, [camera.id]);
  return <div className="journey-camera-photo">
    {failed ? <span>Esta cámara no ha enviado imagen. Prueba de nuevo en unos minutos.</span> : <img src={`/api/camera-image?id=${encodeURIComponent(camera.id)}&t=${version}`} alt={`Foto de la DGT en ${camera.name}`} onError={() => setFailed(true)} />}
    <button type="button" onClick={() => { setFailed(false); setVersion(Date.now()); }}>Pedir otra foto</button>
  </div>;
}

export function CameraTimeline({ items, progressKm = 0, onCameraSelect, activeId, compact = false }) {
  const [expandedId, setExpandedId] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [showPassed, setShowPassed] = useState(false);
  const passedCount = progressKm > 0 ? items.filter((camera) => camera.distanceKm < progressKm - 0.2).length : 0;
  useEffect(() => {
    const index = items.findIndex((camera) => camera.id === activeId);
    if (index < 0) return;
    setExpandedId(activeId);
    if (index < passedCount) setShowPassed(true);
    if (index >= (compact ? 5 : 12)) setShowAll(true);
  }, [activeId, items, passedCount, compact]);
  const candidates = showPassed ? items : items.slice(passedCount);
  const visible = showAll ? candidates : candidates.slice(0, compact ? 5 : 12);
  if (!items.length) return <p className="journey-camera-empty">No hay cámaras DGT próximas a este recorrido.</p>;
  return <div className={`journey-camera-timeline${compact ? " is-compact" : ""}`}>
    {passedCount > 0 && <button className="journey-camera-more" type="button" onClick={() => setShowPassed((value) => !value)}>{showPassed ? "Ocultar cámaras pasadas" : `Ver ${passedCount} cámaras pasadas`}</button>}
    {!candidates.length && <p className="journey-camera-empty">No quedan cámaras DGT por delante en este recorrido.</p>}
    {visible.map((camera) => {
      const remaining = camera.distanceKm - progressKm;
      const passed = remaining < -0.2;
      const open = expandedId === camera.id;
      return <article key={camera.id} className={`journey-camera-row${passed ? " is-passed" : ""}`}>
        <button type="button" className="journey-camera-heading" aria-expanded={open} onClick={() => { setExpandedId(open ? null : camera.id); if (!open) onCameraSelect?.(camera.id); }}>
          <span className="journey-camera-pin" aria-hidden="true" />
          <span className="journey-camera-name"><strong>{camera.name}</strong><small>{camera.province || "España"} · {km(camera.distanceKm)} desde la salida</small></span>
          <b>{passed ? "Pasada" : `${km(Math.max(0, remaining))} por delante`}</b>
        </button>
        {open && <CameraPhoto camera={camera} />}
      </article>;
    })}
    {!showAll && candidates.length > visible.length && <button className="journey-camera-more" type="button" onClick={() => setShowAll(true)}>Ver las {candidates.length - visible.length} cámaras restantes</button>}
  </div>;
}

export default function RouteCameraSection({ cameras, geometry, loading, error, mapLayer, onMapLayerChange, onRetry, onCameraSelect, selectedCameraId }) {
  const [following, setFollowing] = useState(false);
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState("");
  useEffect(() => {
    if (!following) return undefined;
    if (!navigator.geolocation) { setLocationError("Este navegador no permite seguir tu ubicación."); setFollowing(false); return undefined; }
    const watchId = navigator.geolocation.watchPosition(
      ({ coords }) => { setLocation({ lat: coords.latitude, lng: coords.longitude }); setLocationError(""); },
      () => { setLocationError("No se pudo seguir tu ubicación."); setFollowing(false); },
      { enableHighAccuracy: true, maximumAge: 15000, timeout: 20000 },
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, [following]);
  const progress = useMemo(() => location ? projectPointToRoute(location, geometry) : null, [location, geometry]);
  const onRoute = progress && progress.offsetKm <= 3;
  const progressKm = onRoute ? progress.distanceKm : 0;
  const ahead = cameras.filter((camera) => camera.distanceKm >= progressKm - 0.2);
  return <section className="journey-cameras" aria-label="Cámaras del trayecto">
    <div className="journey-camera-title"><h3>Cámaras del trayecto</h3><strong className="count-pill">{loading ? "…" : cameras.length}</strong></div>
    <p>Ordenadas desde la salida. Algunas enfocan el sentido contrario.</p>
    <div className="journey-camera-actions">
      <div className="journey-map-switch" aria-label="Capa del mapa"><button type="button" aria-pressed={mapLayer === "stations"} className={mapLayer === "stations" ? "is-active" : ""} onClick={() => onMapLayerChange("stations")}>Gasolineras</button><button type="button" aria-pressed={mapLayer === "cameras"} className={mapLayer === "cameras" ? "is-active" : ""} onClick={() => onMapLayerChange("cameras")}>Cámaras</button></div>
      <button type="button" className="journey-follow" aria-pressed={following} onClick={() => { setLocationError(""); setFollowing((value) => !value); }}>{following ? "Detener seguimiento" : "Seguir mi viaje"}</button>
    </div>
    {locationError && <p className="journey-camera-note" role="status">{locationError}</p>}
    {following && !location && <p className="journey-camera-note">Buscando tu posición…</p>}
    {following && location && !onRoute && <p className="journey-camera-note">Tu posición está fuera del recorrido calculado. Se muestra el orden desde la salida.</p>}
    {onRoute && <p className="journey-camera-note">Has recorrido aproximadamente {km(progressKm)} · {ahead.length} cámaras por delante{ahead[0] ? ` · próxima a ${km(Math.max(0, ahead[0].distanceKm - progressKm))}` : ""}.</p>}
    {!geometry ? <p className="journey-camera-empty">Esperando una ruta por carretera para localizar las cámaras con precisión.</p>
      : loading ? <p className="journey-camera-empty">Consultando cámaras DGT…</p>
      : error ? <p className="journey-camera-empty">{error} <button type="button" onClick={onRetry}>Reintentar</button></p>
      : <CameraTimeline items={cameras} progressKm={progressKm} onCameraSelect={onCameraSelect} activeId={selectedCameraId} />}
  </section>;
}
